import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getCoach360, money, type Coach360 as Coach360Data, type Period } from "../../services/club360";
import { SectionCard, StatCard, StatGrid } from "../admin/AdminCard";
import { formatDate } from "../../lib/dateFormat";

/**
 * Viziunea 360 a unui antrenor.
 *
 * The questions somebody actually has while looking at a member of staff:
 * which squads are theirs, how many children that comes to, whether the
 * trainings are happening, whether the register is being taken, what they are
 * holding in cash, and who they brought into the club.
 *
 * Two deliberate separations. Sessions ON their teams and sessions they RAN
 * are counted apart, because a team covered by somebody else all month is a
 * finding. And the club's affiliate earnings are kept apart from the coach's
 * own reward: adding them together would overstate both.
 */

const PERIODS: { key: Period; label: string }[] = [
  { key: "month", label: "Luna curentă" },
  { key: "quarter", label: "Ultimele 3 luni" },
  { key: "all", label: "Tot timpul" },
];

export default function Coach360({
  coachId,
  clubId,
}: {
  coachId: string;
  /** Which club to report on. A coach may work at two; one admin sees one. */
  clubId?: string | null;
}) {
  const navigate = useNavigate();

  const [data, setData] = useState<Coach360Data | null>(null);
  const [period, setPeriod] = useState<Period>("quarter");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAmounts, setShowAmounts] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getCoach360(coachId, { clubId, period }));
    } catch (e: any) {
      setError(e?.response?.data?.error || "Nu am putut încărca datele antrenorului.");
      setData(null);
    }
    setLoading(false);
  }, [coachId, clubId, period]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-4 rounded-full border-indigo-600 animate-spin border-t-transparent" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-8 text-center bg-white border rounded-2xl border-slate-200">
        <p className="text-slate-500">{error || "Nu am putut încărca datele antrenorului."}</p>
        <button onClick={load} className="mt-3 text-sm font-semibold text-indigo-600">
          Reîncearcă
        </button>
      </div>
    );
  }

  const { coach, roster, attendance, sessions, teams, affiliate } = data;
  // Named `fees` rather than destructured as `money`, which is the formatter.
  const fees = data.money;

  return (
    <div className="space-y-5">
      {/* Period */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1 p-1 border rounded-xl border-slate-200 bg-slate-100">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`px-3 py-1.5 text-sm font-semibold rounded-lg transition-colors ${
                period === p.key ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <span className="text-sm text-slate-500">
          la {data.club.name}
          {!coach.is_home_club && (
            <span className="ml-1 text-xs text-slate-400">(clubul principal e altul)</span>
          )}
        </span>
        {loading && <span className="text-xs text-slate-400">se actualizează...</span>}
      </div>

      {/* Headline figures */}
      <StatGrid>
        <StatCard
          label="Echipe"
          value={roster.teams}
          dotClass="bg-indigo-500"
          hint={
            roster.teams === 0
              ? "nicio echipă alocată"
              : `${roster.head_of} ca principal${roster.teams > roster.head_of ? `, ${roster.teams - roster.head_of} ca secundar` : ""}`
          }
          hintTone={roster.teams === 0 ? "down" : "muted"}
        />
        <StatCard
          label="Sportivi"
          value={roster.athletes}
          dotClass="bg-sky-500"
          hint={
            roster.average_age !== null ? `media ${roster.average_age} ani` : "fără date de naștere"
          }
          hintTone="muted"
        />
        <StatCard
          label="Prezență"
          value={attendance.marked === 0 ? "—" : `${attendance.rate}%`}
          dotClass={
            attendance.rate >= 75 ? "bg-emerald-500" : attendance.rate >= 50 ? "bg-amber-500" : "bg-rose-500"
          }
          valueClass={
            attendance.marked === 0
              ? "text-slate-400"
              : attendance.rate >= 75
                ? "text-emerald-600"
                : "text-slate-800"
          }
          hint={
            attendance.marked === 0
              ? "nicio prezență pontată"
              : `${attendance.present} prezențe din ${attendance.marked} pontate`
          }
          hintTone={attendance.marked === 0 ? "down" : "muted"}
        />
        <StatCard
          label="Antrenamente"
          value={sessions.in_period}
          dotClass="bg-violet-500"
          hint={
            sessions.in_period === 0
              ? "niciunul în perioadă"
              : sessions.ran_by_them === sessions.in_period
                ? "toate ținute de el"
                : `${sessions.ran_by_them} ținute de el`
          }
          hintTone={sessions.in_period === 0 ? "down" : "muted"}
        />
      </StatGrid>

      {/* Something to fix */}
      {(attendance.unmarked_sessions > 0 ||
        fees.cash_pending_payments > 0 ||
        roster.teams === 0 ||
        coach.email_verified === false) && (
        <div className="p-4 space-y-1 text-sm border rounded-2xl border-amber-200 bg-amber-50/60">
          <p className="mb-1 text-xs font-bold tracking-wide uppercase text-amber-700">De urmărit</p>
          {coach.email_verified === false && (
            <p className="text-amber-800">
              Emailul nu e confirmat — nu a intrat niciodată în cont de unul singur.
            </p>
          )}
          {roster.teams === 0 && (
            <p className="text-amber-800">Nu are nicio echipă alocată, deci nu poate face nimic în aplicație.</p>
          )}
          {attendance.unmarked_sessions > 0 && (
            <p className="text-amber-800">
              {attendance.unmarked_sessions} din {sessions.in_period} antrenamente fără prezență pontată.
            </p>
          )}
          {fees.cash_pending_payments > 0 && (
            <p className="text-amber-800">
              Are {fees.cash_pending_payments} încasări cash nepredate
              {showAmounts ? ` (${money(fees.cash_pending_handover_cents)})` : ""}.
            </p>
          )}
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Who they are here */}
        <SectionCard title="La acest club" bodyPadded>
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-slate-500">Rol</dt>
              <dd className="font-semibold capitalize text-slate-800">
                {coach.membership_role === "coordinator" ? "coordonator" : "antrenor"}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500">De la</dt>
              <dd className="font-semibold text-slate-800">{formatDate(coach.joined_at)}</dd>
            </div>
            <div className="col-span-2">
              <dt className="text-slate-500">Sporturi</dt>
              <dd className="mt-1">
                {coach.sports.length === 0 ? (
                  <span className="text-sm text-amber-700">
                    Niciun sport alocat — nu vede nimic filtrat pe sport.
                  </span>
                ) : (
                  <span className="flex flex-wrap gap-1.5">
                    {coach.sports.map((s) => (
                      <span
                        key={s.id}
                        className="px-2 py-0.5 text-xs font-semibold rounded-full bg-emerald-50 text-emerald-700"
                      >
                        {s.name}
                      </span>
                    ))}
                  </span>
                )}
              </dd>
            </div>
            {(coach.phone || coach.city) && (
              <div className="col-span-2 pt-2 border-t border-slate-100">
                <dt className="text-slate-500">Contact</dt>
                <dd className="font-medium text-slate-800">
                  {coach.phone ? (
                    <a href={`tel:${coach.phone}`} className="text-indigo-600 hover:underline">
                      {coach.phone}
                    </a>
                  ) : (
                    <span className="text-slate-400">fără telefon</span>
                  )}
                  {coach.city && <span className="ml-2 text-slate-500">{coach.city}</span>}
                </dd>
              </div>
            )}
          </dl>
          {coach.bio && <p className="pt-3 mt-3 text-sm border-t border-slate-100 text-slate-600">{coach.bio}</p>}
        </SectionCard>

        {/* Money */}
        <SectionCard
          title="Bani"
          actions={
            <button
              onClick={() => setShowAmounts((v) => !v)}
              className="text-sm font-semibold text-indigo-600 hover:text-indigo-700"
            >
              {showAmounts ? "Ascunde sumele" : "Arată sumele"}
            </button>
          }
          bodyPadded
        >
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-slate-500">De încasat la echipele lui</dt>
              <dd className={`font-semibold ${fees.outstanding_cents > 0 ? "text-amber-600" : "text-slate-800"}`}>
                {showAmounts
                  ? money(fees.outstanding_cents)
                  : fees.outstanding_cents > 0
                    ? "•••"
                    : "0"}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500">Restanțieri</dt>
              <dd className={`font-semibold ${fees.overdue_athletes > 0 ? "text-rose-600" : "text-slate-800"}`}>
                {fees.overdue_athletes}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500">Cash strâns de el</dt>
              <dd className="font-semibold text-slate-800">
                {showAmounts ? money(fees.cash_collected_cents) : "•••"}
                <span className="ml-1 text-xs font-normal text-slate-400">
                  ({fees.cash_payments} plăți)
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-slate-500">Nepredat clubului</dt>
              <dd
                className={`font-semibold ${
                  fees.cash_pending_handover_cents > 0 ? "text-rose-600" : "text-slate-800"
                }`}
              >
                {showAmounts ? money(fees.cash_pending_handover_cents) : fees.cash_pending_payments > 0 ? "•••" : "0"}
              </dd>
            </div>
          </dl>
          <p className="mt-3 text-[11px] text-slate-400">
            „Cash strâns" e pe toată perioada aleasă. „Nepredat" e pe tot istoricul — banii care nu
            au ajuns încă la club nu expiră la finalul lunii.
          </p>
        </SectionCard>

        {/* Trainings */}
        <SectionCard title="Antrenamente" bodyPadded>
          <dl className="grid grid-cols-3 gap-3 text-sm">
            <div>
              <dt className="text-slate-500">În perioadă</dt>
              <dd className="text-lg font-bold text-slate-800">{sessions.in_period}</dd>
            </div>
            <div>
              <dt className="text-slate-500">Programate</dt>
              <dd className="text-lg font-bold text-slate-800">{sessions.upcoming}</dd>
            </div>
            <div>
              <dt className="text-slate-500">Total</dt>
              <dd className="text-lg font-bold text-slate-800">{sessions.total}</dd>
            </div>
          </dl>

          {sessions.last_at ? (
            <p className="mt-3 text-sm text-slate-600">
              Ultimul: <span className="font-medium">{formatDate(sessions.last_at)}</span>
              {sessions.last_team ? ` · ${sessions.last_team}` : ""}
            </p>
          ) : (
            <p className="mt-3 text-sm text-slate-500">Nicio sesiune ținută încă.</p>
          )}

          {sessions.recent.length > 0 && (
            <ul className="mt-3 divide-y divide-slate-100">
              {sessions.recent.slice(0, 6).map((s) => (
                <li key={s.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                  <span className="font-medium text-slate-700">{formatDate(s.start_at)}</span>
                  <span className="min-w-0 truncate text-slate-500">{s.team_name || s.title}</span>
                  <span className="ml-auto flex items-center gap-2">
                    {!s.theirs && (
                      <span className="text-[11px] font-semibold text-slate-400">alt antrenor</span>
                    )}
                    <span
                      className={`text-[11px] font-bold ${s.marked ? "text-emerald-600" : "text-amber-600"}`}
                    >
                      {s.marked ? "pontat" : "nepontat"}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        {/* Affiliate */}
        <SectionCard title="Afiliere" bodyPadded>
          {affiliate.referred === 0 ? (
            <p className="text-sm text-slate-500">Nu a adus nimeni prin codul lui de afiliere.</p>
          ) : (
            <div className="space-y-3 text-sm">
              <dl className="grid grid-cols-2 gap-3">
                <div>
                  <dt className="text-slate-500">Aduși</dt>
                  <dd className="font-semibold text-slate-800">
                    {affiliate.referred}
                    <span className="ml-1 font-normal text-slate-400">
                      ({affiliate.converted} convertiți)
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-500">Câștigul clubului</dt>
                  <dd className="font-semibold text-emerald-700">
                    {showAmounts ? money(affiliate.club_earned_cents) : "•••"}
                  </dd>
                </div>
                <div className="col-span-2">
                  <dt className="text-slate-500">Recompensa lui personală</dt>
                  <dd className="font-semibold text-slate-800">
                    {showAmounts ? money(affiliate.own_earned_cents) : "•••"}
                  </dd>
                </div>
              </dl>
              <p className="pt-2 text-[11px] border-t border-slate-100 text-slate-400">
                Două sume diferite: ce a acumulat clubul prin el și ce a acumulat el. Nu se adună.
              </p>
            </div>
          )}
        </SectionCard>
      </div>

      {/* The squads */}
      <SectionCard title={`Echipele lui (${teams.length})`}>
        {teams.length === 0 ? (
          <p className="px-5 py-6 text-sm text-slate-500">
            Nicio echipă alocată. Îi aloci una din pagina echipei, la „Editează".
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-slate-200 bg-slate-50">
                  <th className="px-4 py-3 font-semibold text-slate-600">Echipă</th>
                  <th className="px-4 py-3 font-semibold text-right text-slate-600">Sportivi</th>
                  <th className="px-4 py-3 font-semibold text-right text-slate-600">Antrenamente</th>
                  <th className="px-4 py-3 font-semibold text-right text-slate-600">Prezență</th>
                  <th className="hidden px-4 py-3 font-semibold text-right md:table-cell text-slate-600">
                    De încasat
                  </th>
                </tr>
              </thead>
              <tbody>
                {teams.map((tm) => (
                  <tr key={tm.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60">
                    <td className="px-4 py-3">
                      <button
                        onClick={() => navigate(`/dashboard/admin-club/teams/${tm.id}`)}
                        className="font-medium text-left text-slate-800 hover:text-indigo-600"
                      >
                        {tm.name}
                      </button>
                      <span className="block text-[11px] text-slate-500">
                        {[tm.sport, tm.age_group, tm.is_head ? "principal" : "secundar"]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-700">{tm.athletes}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                      {tm.sessions_in_period}
                      {tm.unmarked_sessions > 0 && (
                        <span className="block text-[11px] font-semibold text-amber-600">
                          {tm.unmarked_sessions} nepontate
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {tm.attendance_marked === 0 ? (
                        <span className="text-slate-300">—</span>
                      ) : (
                        <span
                          className={`font-semibold tabular-nums ${
                            tm.attendance_rate >= 75
                              ? "text-emerald-700"
                              : tm.attendance_rate >= 50
                                ? "text-amber-600"
                                : "text-rose-600"
                          }`}
                        >
                          {tm.attendance_rate}%
                        </span>
                      )}
                    </td>
                    <td className="hidden px-4 py-3 text-right md:table-cell">
                      {tm.outstanding_cents === 0 ? (
                        <span className="text-slate-400">0</span>
                      ) : showAmounts ? (
                        <span className="font-semibold text-amber-600">{money(tm.outstanding_cents)}</span>
                      ) : (
                        <span className="text-slate-400">•••</span>
                      )}
                      {tm.overdue_athletes > 0 && (
                        <span className="block text-[11px] font-semibold text-rose-600">
                          {tm.overdue_athletes} restanțieri
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
